import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  RefreshCw, Phone, PhoneCall, TrendingUp, TrendingDown,
  Users, Target, DollarSign, Clock, Activity, AlertTriangle,
  ChevronUp, ChevronDown, BarChart3, Eye, ArrowRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine, Cell, Line, ComposedChart
} from 'recharts';

// ── HELPERS ──────────────────────────────────────────────────────────────────

/** Get today's date in IST (YYYY-MM-DD) */
const getISTDate = () => {
  const now = new Date();
  const istMs = now.getTime() + (5.5 * 60 * 60 * 1000);
  return new Date(istMs).toISOString().split('T')[0];
};

/** Get current 30-min slot in IST for the "NOW" marker */
const getCurrentSlot = () => {
  const now = new Date();
  const istHour = (now.getUTCHours() + 5 + Math.floor((now.getUTCMinutes() + 30) / 60)) % 24;
  const istMin = (now.getUTCMinutes() + 30) % 60;
  return `${String(istHour).padStart(2, '0')}:${istMin < 30 ? '00' : '30'}`;
};

/** Format relative time since last update */
const formatRelativeTime = (date) => {
  if (!date) return 'never';
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

/** Safe percentage calculation */
const safePct = (num, den) => {
  if (!den || den === 0) return 0;
  return Math.round(((num || 0) / den) * 100 * 10) / 10;
};

// ── SORT ICON ────────────────────────────────────────────────────────────────

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <ChevronUp className="w-3 h-3 text-slate-600 opacity-40" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 text-blue-400" />
    : <ChevronDown className="w-3 h-3 text-blue-400" />;
};

// ── DATA FRESHNESS DOT ──────────────────────────────────────────────────────

const FreshnessDot = ({ status, label }) => {
  const color = status === 'live' ? 'bg-emerald-400' : status === 'cached' ? 'bg-amber-400' : 'bg-slate-500';
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      {label}
    </span>
  );
};

// ── CUSTOM RECHARTS TOOLTIP ─────────────────────────────────────────────────

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 shadow-xl">
      <div className="text-xs text-slate-300 font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-white font-semibold">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function SalesFunnel() {
  // ── STATE ──
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getISTDate());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [relativeTime, setRelativeTime] = useState('never');

  // Filters
  const [managers, setManagers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [selectedManager, setSelectedManager] = useState('all');
  const [selectedTL, setSelectedTL] = useState('all');
  const [selectedGym, setSelectedGym] = useState('all');

  // Sorting
  const [sortField, setSortField] = useState('ivr_dials');
  const [sortDir, setSortDir] = useState('desc');
  const [error, setError] = useState(false);
  const abortRef = React.useRef(null);

  // ── FETCH MASTER DATA (managers, TLs, gyms) ──

  useEffect(() => {
    Promise.all([
      axiosInstance.get('/managers'),
      axiosInstance.get('/team-leaders'),
      axiosInstance.get('/gyms'),
    ]).then(([m, tl, g]) => {
      setManagers(m.data || []);
      setTeamLeaders(tl.data || []);
      setGyms((g.data || []).filter(gym => gym.status === 'Active'));
    }).catch(() => {
      // Silently handle - filters just won't populate
    });
  }, []);

  // ── CASCADING FILTER LOGIC ──

  const filteredTLs = useMemo(() => {
    if (selectedManager === 'all') return teamLeaders;
    return teamLeaders.filter(tl => tl.manager_id === selectedManager);
  }, [teamLeaders, selectedManager]);

  const filteredGyms = useMemo(() => {
    let result = gyms;
    if (selectedManager !== 'all') result = result.filter(g => g.manager_id === selectedManager);
    if (selectedTL !== 'all') result = result.filter(g => g.tl_id === selectedTL);
    return result;
  }, [gyms, selectedManager, selectedTL]);

  // Reset child filters when parent changes
  const handleManagerChange = useCallback((value) => {
    setSelectedManager(value);
    setSelectedTL('all');
    setSelectedGym('all');
  }, []);

  const handleTLChange = useCallback((value) => {
    setSelectedTL(value);
    setSelectedGym('all');
  }, []);

  const hasActiveFilters = selectedManager !== 'all' || selectedTL !== 'all' || selectedGym !== 'all';

  // ── FETCH FUNNEL DATA ──

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (selectedManager !== 'all') params.set('manager_id', selectedManager);
      if (selectedTL !== 'all') params.set('tl_id', selectedTL);
      if (selectedGym !== 'all') params.set('gym_id', selectedGym);

      const response = await axiosInstance.get(`/sales-funnel/v2?${params.toString()}`, {
        signal: controller.signal
      });
      if (!controller.signal.aborted) {
        setData(response.data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      if (!controller.signal.aborted) {
        toast.error('Failed to load sales funnel data');
        setError(true);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [selectedDate, selectedManager, selectedTL, selectedGym]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(fetchData, 300000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchData]);

  // Update relative time display every 10s
  useEffect(() => {
    const tick = setInterval(() => {
      setRelativeTime(formatRelativeTime(lastUpdated));
    }, 10000);
    setRelativeTime(formatRelativeTime(lastUpdated));
    return () => clearInterval(tick);
  }, [lastUpdated]);

  // ── DERIVED DATA ──

  const funnel = data?.funnel || {};
  const conversionRates = data?.conversion_rates || {};
  const timeline = data?.timeline || [];
  const pulseBreakdown = data?.pulse_breakdown || [];
  const sourceComparison = data?.source_comparison || {};
  const freshness = data?.data_freshness || {};

  // Funnel visualization data
  const funnelData = useMemo(() => [
    { stage: 'Dialed', value: funnel.ivr_dials || 0, fill: '#3b82f6', source: 'IVR' },
    { stage: 'Connected', value: funnel.ivr_connected || 0, fill: '#22c55e', source: 'IVR' },
    { stage: 'Visit Sched', value: funnel.wtf_visits_scheduled || 0, fill: '#8b5cf6', source: 'WTF' },
    { stage: 'Visit Done', value: funnel.wtf_visits_done || 0, fill: '#f59e0b', source: 'WTF' },
    { stage: 'Closures', value: funnel.wtf_total_closures || 0, fill: '#ef4444', source: 'WTF' },
  ], [funnel]);

  // Conversion arrows between funnel stages
  const funnelConversions = useMemo(() => {
    const d = funnel.ivr_dials || 0;
    const c = funnel.ivr_connected || 0;
    const vs = funnel.wtf_visits_scheduled || 0;
    const vd = funnel.wtf_visits_done || 0;
    const cl = funnel.wtf_total_closures || 0;
    return [
      { from: 'Dialed', to: 'Connected', rate: safePct(c, d) },
      { from: 'Connected', to: 'Visit Sched', rate: safePct(vs, c) },
      { from: 'Visit Sched', to: 'Visit Done', rate: safePct(vd, vs) },
      { from: 'Visit Done', to: 'Closures', rate: safePct(cl, vd) },
    ];
  }, [funnel]);

  // Sorted gym table - compute connect_rate from IVR data
  const sortedGyms = useMemo(() => {
    return [...(data?.per_gym || [])].map(g => ({
      ...g,
      connect_rate: g.ivr_dials > 0 ? Math.round((g.ivr_connected / g.ivr_dials) * 1000) / 10 : 0,
      wtf_walkins: g.wtf_walkins || 0,
      new_sub_revenue: g.wtf_new_subs || 0,
      wtf_sales_amount: g.wtf_sales_amount || 0,
    })).sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [data?.per_gym, sortField, sortDir]);

  const handleSort = useCallback((field) => {
    setSortDir(prev => sortField === field ? (prev === 'desc' ? 'asc' : 'desc') : 'desc');
    setSortField(field);
  }, [sortField]);

  // Connect rate color helper
  const connectRateColor = (rate) => {
    if (rate >= 70) return 'text-emerald-400';
    if (rate >= 50) return 'text-amber-400';
    return 'text-red-400';
  };

  // ── LOADING STATE ──

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-950 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-slate-800 rounded-lg w-1/3" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-slate-800 rounded-xl" />)}
          </div>
          <div className="h-64 bg-slate-800 rounded-xl" />
          <div className="h-80 bg-slate-800 rounded-xl" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-48 bg-slate-800 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
          <h2 className="text-lg font-semibold text-slate-200">Failed to load Sales Funnel data</h2>
          <p className="text-sm text-slate-400">Check your connection and try again</p>
          <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sales-funnel-page" className="min-h-screen bg-slate-950 text-slate-100">

      {/* ════════════════════════════════════════════════════════════════════════
          SECTION 1: STICKY HEADER BAR
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800 px-4 py-3">
        <div className="max-w-[1600px] mx-auto">
          {/* Row 1: Title + freshness + refresh */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-3">
            <div className="flex items-center gap-3">
              <Activity className="w-6 h-6 text-blue-400" />
              <h1 className="text-xl font-bold text-white tracking-tight">Sales Funnel - Live Tracker</h1>
              <div className="hidden sm:flex items-center gap-2 ml-3">
                <FreshnessDot status={freshness.ivr_available ? (freshness.date_is_today ? 'live' : 'cached') : 'offline'} label="IVR" />
                <FreshnessDot status={freshness.wtf_cache_available ? 'cached' : 'offline'} label="WTF" />
                <FreshnessDot status={freshness.pulse_entries_count > 0 ? 'live' : 'cached'} label="Pulse" />
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              <span>Updated {relativeTime}</span>
              <button
                onClick={() => setAutoRefresh(prev => !prev)}
                className={`ml-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
                  autoRefresh
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-slate-700/50 text-slate-500 border border-slate-600/50'
                }`}
              >
                {autoRefresh ? 'Auto' : 'Manual'}
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchData}
                disabled={loading}
                className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Row 2: Date + Filters */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Date</Label>
              <Input
                type="date"
                value={selectedDate}
                max={getISTDate()}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-36 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200 focus:border-blue-500"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Manager</Label>
              <Select value={selectedManager} onValueChange={handleManagerChange}>
                <SelectTrigger className="w-40 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue placeholder="All Managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {managers.map(m => (
                    <SelectItem key={m.manager_id} value={m.manager_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Team Leader</Label>
              <Select value={selectedTL} onValueChange={handleTLChange}>
                <SelectTrigger className="w-40 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue placeholder="All TLs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All TLs</SelectItem>
                  {filteredTLs.map(tl => (
                    <SelectItem key={tl.tl_id} value={tl.tl_id}>{tl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Gym</Label>
              <Select value={selectedGym} onValueChange={setSelectedGym}>
                <SelectTrigger className="w-44 h-8 text-xs bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue placeholder="All Gyms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Gyms</SelectItem>
                  {filteredGyms.map(g => (
                    <SelectItem key={g.gym_id} value={g.gym_id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedManager('all'); setSelectedTL('all'); setSelectedGym('all'); }}
                className="h-8 text-xs text-slate-400 hover:text-white"
              >
                Clear Filters
              </Button>
            )}
            {data?.filters_applied && (
              <span className="text-[10px] text-slate-500 ml-auto">
                {data.filters_applied.gym_count || 0} gym(s)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ══ MAIN CONTENT ══ */}
      <div className="max-w-[1600px] mx-auto px-4 py-6 space-y-6">

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 2: KPI SUMMARY CARDS
            ════════════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Card 1: IVR Dials */}
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-blue-500" />
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Phone className="w-3.5 h-3.5" />
              <span>IVR DIALS</span>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{(funnel.ivr_dials || 0).toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 mt-1">Source: IVR {freshness.date_is_today ? 'Live' : 'Historical'}</div>
            {(funnel.ivr_unmapped_dials || 0) > 0 && (
              <div className="text-[10px] text-amber-400/70 mt-0.5">{funnel.ivr_unmapped_dials} unmapped</div>
            )}
          </div>

          {/* Card 2: IVR Connected */}
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500" />
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <PhoneCall className="w-3.5 h-3.5" />
              <span>IVR CONNECTED</span>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{(funnel.ivr_connected || 0).toLocaleString()}</div>
            <div className="text-[10px] text-slate-500 mt-1">Source: IVR {freshness.date_is_today ? 'Live' : 'Historical'}</div>
          </div>

          {/* Card 3: Connect Rate */}
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 relative overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-1 ${
              (funnel.ivr_connect_rate || 0) >= 70 ? 'bg-emerald-500' : 'bg-amber-500'
            }`} />
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>CONNECT RATE</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-bold tabular-nums ${
                (funnel.ivr_connect_rate || 0) >= 70 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {funnel.ivr_connect_rate || 0}%
              </span>
              <span className="text-xs text-slate-500">/ 70%</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Target: 70%</div>
          </div>

          {/* Card 4: Visits Done */}
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-violet-500" />
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Users className="w-3.5 h-3.5" />
              <span>VISITS DONE</span>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{funnel.wtf_visits_done || 0}</div>
            <div className="text-[10px] text-slate-500 mt-1">
              {freshness.date_is_today ? 'Source: WTF API' : 'WTF data: today only'}
            </div>
          </div>

          {/* Card 5: Total Closures (WTF new subscriptions) */}
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-orange-500" />
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Target className="w-3.5 h-3.5" />
              <span>TOTAL CLOSURES</span>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{funnel.wtf_total_closures || 0}</div>
            <div className="text-[10px] text-emerald-400/70 mt-1">Source: WTF New Subscriptions</div>
          </div>

          {/* Card 6: WTF Actual Sales (Revenue) */}
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-400" />
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <DollarSign className="w-3.5 h-3.5" />
              <span>WTF SALES (INR)</span>
            </div>
            <div className="text-2xl font-bold text-emerald-400 tabular-nums">
              {(funnel.wtf_actual_sales || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {freshness.date_is_today ? 'Source: WTF Ground Truth' : 'WTF data: today only'}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 3: FUNNEL VISUALIZATION
            ════════════════════════════════════════════════════════════════════════ */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800">
            <BarChart3 className="w-4 h-4 text-blue-400" />
            <h2 className="text-sm font-semibold text-slate-200">Sales Funnel Stages</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Horizontal Bar Chart */}
              <div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart layout="vertical" data={funnelData} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                    <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="stage" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip content={<DarkTooltip />} />
                    <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                      {funnelData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Conversion rates between stages */}
              <div className="flex flex-col justify-center space-y-3">
                {funnelConversions.map((conv, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-24 text-right">{conv.from}</span>
                    <div className="flex-1 relative">
                      <div className="h-6 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${Math.min(conv.rate, 100)}%`,
                            background: conv.rate >= 60 ? 'linear-gradient(90deg, #22c55e, #16a34a)' :
                              conv.rate >= 40 ? 'linear-gradient(90deg, #f59e0b, #d97706)' :
                              'linear-gradient(90deg, #ef4444, #dc2626)'
                          }}
                        />
                      </div>
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
                        {conv.rate}%
                      </span>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-600" />
                    <span className="text-xs text-slate-400 w-24">{conv.to}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 4: 30-MINUTE ACTIVITY TIMELINE
            ════════════════════════════════════════════════════════════════════════ */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800">
            <Clock className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-200">30-Minute Activity Timeline</h2>
            <span className="text-[10px] text-slate-500 ml-2">IVR call volume + talk minutes by 30-min slots</span>
          </div>
          <div className="p-5">
            {timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={timeline} barCategoryGap="8%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="slot" stroke="#94a3b8" tick={{ fontSize: 10 }} interval={1} />
                  <YAxis yAxisId="calls" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="minutes" orientation="right" stroke="#f59e0b" tick={{ fontSize: 10 }} />
                  <Tooltip content={<DarkTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Bar yAxisId="calls" dataKey="dials" fill="#3b82f6" name="Dials" radius={[2, 2, 0, 0]} />
                  <Bar yAxisId="calls" dataKey="connected" fill="#22c55e" name="Connected" radius={[2, 2, 0, 0]} />
                  <Line yAxisId="minutes" type="monotone" dataKey="minutes" stroke="#f59e0b" name="Talk Minutes" dot={false} strokeWidth={2} />
                  {/* Pulse window boundary lines */}
                  <ReferenceLine yAxisId="calls" x="12:00" stroke="#6366f1" strokeDasharray="5 5" label={{ value: "P2", position: "top", fill: "#6366f1", fontSize: 10 }} />
                  <ReferenceLine yAxisId="calls" x="16:00" stroke="#6366f1" strokeDasharray="5 5" label={{ value: "P3", position: "top", fill: "#6366f1", fontSize: 10 }} />
                  <ReferenceLine yAxisId="calls" x="20:00" stroke="#6366f1" strokeDasharray="5 5" label={{ value: "P4", position: "top", fill: "#6366f1", fontSize: 10 }} />
                  {/* Current time marker */}
                  <ReferenceLine yAxisId="calls" x={getCurrentSlot()} stroke="#f97316" strokeWidth={2} label={{ value: "NOW", position: "top", fill: "#f97316", fontSize: 11, fontWeight: "bold" }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-500 text-sm">
                No timeline data available for this date
              </div>
            )}
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 5: PULSE COMPARISON CARDS (P1-P4)
            ════════════════════════════════════════════════════════════════════════ */}
        {pulseBreakdown.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-violet-400" />
              <h2 className="text-sm font-semibold text-slate-200">Pulse Window Comparison</h2>
              <span className="text-[10px] text-slate-500 ml-2">IVR vs Pulse manual entries per window</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {pulseBreakdown.map((p) => {
                const dialGapBad = Math.abs(p.discrepancy_dials || 0) > (p.ivr_dials || 1) * 0.2;
                const connGapBad = Math.abs(p.discrepancy_connected || 0) > (p.ivr_connected || 1) * 0.2;
                return (
                  <div key={p.pulse_id} className="bg-slate-900/80 border border-slate-700/50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-bold text-slate-200">{p.pulse_id}</span>
                      <span className="text-[10px] text-slate-500">{p.label}</span>
                    </div>
                    <div className="space-y-2">
                      {/* Dials comparison */}
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">IVR Dials</span>
                        <span className="text-blue-400 font-semibold tabular-nums">{(p.ivr_dials || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Pulse Dials</span>
                        <span className="text-slate-300 tabular-nums">{(p.pulse_dialed || 0).toLocaleString()}</span>
                      </div>
                      <div className={`flex justify-between text-sm font-bold ${dialGapBad ? 'text-red-400' : 'text-emerald-400'}`}>
                        <span>Gap</span>
                        <span className="tabular-nums">{(p.discrepancy_dials || 0) > 0 ? '+' : ''}{p.discrepancy_dials || 0}</span>
                      </div>

                      {/* Connected comparison */}
                      <div className="border-t border-slate-800 pt-2 mt-2 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">IVR Conn</span>
                          <span className="text-emerald-400 font-semibold tabular-nums">{(p.ivr_connected || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Pulse Conn</span>
                          <span className="text-slate-300 tabular-nums">{(p.pulse_connected || 0).toLocaleString()}</span>
                        </div>
                        <div className={`flex justify-between text-sm font-bold ${connGapBad ? 'text-red-400' : 'text-emerald-400'}`}>
                          <span>Gap</span>
                          <span className="tabular-nums">{(p.discrepancy_connected || 0) > 0 ? '+' : ''}{p.discrepancy_connected || 0}</span>
                        </div>
                      </div>

                      {/* Closures */}
                      <div className="border-t border-slate-700 pt-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Closures</span>
                          <span className="text-orange-400 font-bold tabular-nums">{(p.closures || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 6: PER-GYM TABLE
            ════════════════════════════════════════════════════════════════════════ */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-cyan-400" />
              <h2 className="text-sm font-semibold text-slate-200">Per-Gym Breakdown</h2>
              <span className="text-[10px] text-slate-500 ml-2">{sortedGyms.length} gym(s)</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-slate-800/60">
                  {[
                    { key: 'gym_name', label: 'Gym', align: 'left' },
                    { key: 'ivr_dials', label: 'IVR Dials' },
                    { key: 'ivr_connected', label: 'IVR Conn' },
                    { key: 'connect_rate', label: 'Conn%' },
                    { key: 'wtf_walkins', label: 'Walk-ins' },
                    { key: 'new_sub_revenue', label: 'New Subs (₹)' },
                    { key: 'wtf_sales_amount', label: 'Sales (INR)' },
                  ].map(col => (
                    <th
                      key={col.key}
                      className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400 cursor-pointer select-none hover:text-slate-200 transition-colors ${
                        col.align === 'left' ? 'text-left' : 'text-center'
                      }`}
                      onClick={() => handleSort(col.key)}
                    >
                      <div className={`flex items-center gap-1 ${col.align === 'left' ? '' : 'justify-center'}`}>
                        {col.label}
                        <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedGyms.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-sm text-slate-500">
                      No gym data available
                    </td>
                  </tr>
                ) : (
                  sortedGyms.map((gym, idx) => (
                    <tr
                      key={gym.gym_id || idx}
                      className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-4 py-2.5 text-sm font-medium text-slate-200">{gym.gym_name || gym.gym_id}</td>
                      <td className="px-4 py-2.5 text-sm text-center text-blue-400 font-semibold tabular-nums">{(gym.ivr_dials || 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-sm text-center text-emerald-400 font-semibold tabular-nums">{(gym.ivr_connected || 0).toLocaleString()}</td>
                      <td className={`px-4 py-2.5 text-sm text-center font-bold tabular-nums ${connectRateColor(gym.connect_rate || 0)}`}>
                        {(gym.connect_rate || 0).toFixed(1)}%
                      </td>
                      <td className="px-4 py-2.5 text-sm text-center text-amber-400 tabular-nums">{(gym.wtf_walkins || 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-sm text-center text-orange-400 font-bold tabular-nums">{gym.new_sub_revenue ? gym.new_sub_revenue.toLocaleString('en-IN') : '-'}</td>
                      <td className="px-4 py-2.5 text-sm text-center text-emerald-400 font-bold tabular-nums">
                        {(gym.wtf_sales_amount || 0) > 0
                          ? (gym.wtf_sales_amount).toLocaleString('en-IN')
                          : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 7: SOURCE DATA COMPARISON (IVR vs Pulse)
            ════════════════════════════════════════════════════════════════════════ */}
        <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-200">Source Data Comparison</h2>
            <span className="text-[10px] text-slate-500 ml-2">IVR (Actual) vs Pulse (Manual Entry) discrepancies</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Dials Comparison */}
              <div>
                <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3">Dials</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <div className="text-[10px] text-blue-400 mb-1 uppercase tracking-wider">IVR Dials (Actual)</div>
                    <div className="text-2xl font-bold text-blue-400 tabular-nums">{(sourceComparison.dials?.ivr || 0).toLocaleString()}</div>
                  </div>
                  <div className="text-center p-4 bg-slate-500/10 rounded-lg border border-slate-500/20">
                    <div className="text-[10px] text-slate-400 mb-1 uppercase tracking-wider">Pulse Dials (Manual)</div>
                    <div className="text-2xl font-bold text-slate-300 tabular-nums">{(sourceComparison.dials?.pulse || 0).toLocaleString()}</div>
                  </div>
                </div>
                {sourceComparison.dials && (
                  <div className={`mt-3 text-center text-sm font-bold ${
                    Math.abs(sourceComparison.dials.delta_pct || 0) > 10 ? 'text-red-400' : 'text-emerald-400'
                  }`}>
                    Delta: {(sourceComparison.dials.delta || 0) > 0 ? '+' : ''}{sourceComparison.dials.delta || 0}
                    {' '}({(sourceComparison.dials.delta_pct || 0) > 0 ? '+' : ''}{sourceComparison.dials.delta_pct || 0}%)
                    {Math.abs(sourceComparison.dials.delta_pct || 0) > 10 && (
                      <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />
                    )}
                  </div>
                )}
              </div>

              {/* Connected Comparison */}
              <div>
                <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3">Connected</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <div className="text-[10px] text-emerald-400 mb-1 uppercase tracking-wider">IVR Conn (Actual)</div>
                    <div className="text-2xl font-bold text-emerald-400 tabular-nums">{(sourceComparison.connected?.ivr || 0).toLocaleString()}</div>
                  </div>
                  <div className="text-center p-4 bg-slate-500/10 rounded-lg border border-slate-500/20">
                    <div className="text-[10px] text-slate-400 mb-1 uppercase tracking-wider">Pulse Conn (Manual)</div>
                    <div className="text-2xl font-bold text-slate-300 tabular-nums">{(sourceComparison.connected?.pulse || 0).toLocaleString()}</div>
                  </div>
                </div>
                {sourceComparison.connected && (
                  <div className={`mt-3 text-center text-sm font-bold ${
                    Math.abs(sourceComparison.connected.delta_pct || 0) > 10 ? 'text-red-400' : 'text-emerald-400'
                  }`}>
                    Delta: {(sourceComparison.connected.delta || 0) > 0 ? '+' : ''}{sourceComparison.connected.delta || 0}
                    {' '}({(sourceComparison.connected.delta_pct || 0) > 0 ? '+' : ''}{sourceComparison.connected.delta_pct || 0}%)
                    {Math.abs(sourceComparison.connected.delta_pct || 0) > 10 && (
                      <AlertTriangle className="w-3.5 h-3.5 inline ml-1" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════════
            SECTION 8: CONVERSION RATES TABLE
            ════════════════════════════════════════════════════════════════════════ */}
        {Object.keys(conversionRates).length > 0 && (
          <div className="bg-slate-900/80 border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <h2 className="text-sm font-semibold text-slate-200">Conversion Rate Targets</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/60">
                    <th className="px-5 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Metric</th>
                    <th className="px-5 py-2.5 text-center text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Actual</th>
                    <th className="px-5 py-2.5 text-center text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Target</th>
                    <th className="px-5 py-2.5 text-center text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Source</th>
                    <th className="px-5 py-2.5 text-center text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(conversionRates).map(([key, rate]) => {
                    const actual = rate?.actual || 0;
                    const target = rate?.target || 0;
                    const met = actual >= target;
                    const close = actual >= target * 0.85;
                    return (
                      <tr key={key} className="border-b border-slate-800 hover:bg-slate-800/40">
                        <td className="px-5 py-3 text-sm font-medium text-slate-200 capitalize">
                          {key.replace(/_/g, ' ')}
                        </td>
                        <td className={`px-5 py-3 text-sm text-center font-bold tabular-nums ${met ? 'text-emerald-400' : close ? 'text-amber-400' : 'text-red-400'}`}>
                          {actual}%
                        </td>
                        <td className="px-5 py-3 text-sm text-center text-slate-400 tabular-nums">
                          {target}%
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                            {rate?.source || '-'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          {met ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-400">
                              <TrendingUp className="w-3 h-3" /> On Target
                            </span>
                          ) : close ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-400">
                              <AlertTriangle className="w-3 h-3" /> Close
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-red-400">
                              <TrendingDown className="w-3 h-3" /> Below
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
